import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, Sparkle } from "@phosphor-icons/react";

const EMPTY_DOSE = { indication: "", min_mg: "", typical_mg: "", max_mg: "", unit: "mg", frequency: "1x/día", notes: "" };

export default function CompoundForm() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const editing = !!slug;

    const [name, setName] = useState("");
    const [slugVal, setSlugVal] = useState("");
    const [aka, setAka] = useState("");
    const [category, setCategory] = useState("");
    const [indications, setIndications] = useState("");
    const [contraindications, setContraindications] = useState("");
    const [interactions, setInteractions] = useState("");
    const [upperLimit, setUpperLimit] = useState("");
    const [doseRanges, setDoseRanges] = useState([]);
    const [euNotes, setEuNotes] = useState("");
    const [summary, setSummary] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [generating, setGenerating] = useState(false);

    const generateProfile = async () => {
        if (!name.trim()) {
            toast.error("Escribe primero el nombre del principio activo");
            return;
        }
        setGenerating(true);
        try {
            const { data: p } = await api.post("/compounds/generate-profile", { name: name.trim() });
            if (p.name) setName(p.name);
            setAka((p.aka || []).join(", "));
            if (p.category) setCategory(p.category);
            setIndications((p.indications || []).join(", "));
            setContraindications((p.contraindications || []).join("\n"));
            setInteractions((p.interactions || []).join("\n"));
            setUpperLimit(p.upper_limit_mg ?? "");
            setDoseRanges((p.dose_ranges || []).map((d) => ({
                indication: d.indication || "",
                min_mg: d.min_mg ?? "", typical_mg: d.typical_mg ?? "", max_mg: d.max_mg ?? "",
                unit: d.unit || "mg", frequency: d.frequency || "1x/día", notes: d.notes || "",
            })));
            setEuNotes(p.eu_regulatory_notes || "");
            setSummary(p.summary || "");
            toast.success("Perfil generado — revisa y ajusta antes de guardar");
        } catch (err) {
            toast.error(err.response?.data?.detail || "La IA no pudo generar el perfil");
        } finally { setGenerating(false); }
    };

    useEffect(() => {
        if (editing) {
            api.get(`/compounds/${slug}`).then((r) => {
                const c = r.data;
                setName(c.name); setSlugVal(c.slug); setAka((c.aka || []).join(", "));
                setCategory(c.category || "");
                setIndications((c.indications || []).join(", "));
                setContraindications((c.contraindications || []).join("\n"));
                setInteractions((c.interactions || []).join("\n"));
                setUpperLimit(c.upper_limit_mg ?? "");
                setDoseRanges(c.dose_ranges || []);
                setEuNotes(c.eu_regulatory_notes || "");
                setSummary(c.summary || "");
            });
        }
    }, [slug, editing]);

    const addDose = () => setDoseRanges([...doseRanges, { ...EMPTY_DOSE }]);
    const updateDose = (i, k, v) => {
        const next = [...doseRanges];
        next[i] = { ...next[i], [k]: v };
        setDoseRanges(next);
    };
    const removeDose = (i) => setDoseRanges(doseRanges.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload = {
            slug: editing ? slug : (slugVal || undefined),
            name,
            aka: aka.split(",").map((s) => s.trim()).filter(Boolean),
            category,
            indications: indications.split(",").map((s) => s.trim()).filter(Boolean),
            contraindications: contraindications.split("\n").map((s) => s.trim()).filter(Boolean),
            interactions: interactions.split("\n").map((s) => s.trim()).filter(Boolean),
            upper_limit_mg: upperLimit === "" ? null : parseFloat(upperLimit),
            dose_ranges: doseRanges.map((d) => ({
                ...d,
                min_mg: d.min_mg === "" ? null : parseFloat(d.min_mg),
                typical_mg: d.typical_mg === "" ? null : parseFloat(d.typical_mg),
                max_mg: d.max_mg === "" ? null : parseFloat(d.max_mg),
            })),
            eu_regulatory_notes: euNotes || null,
            summary: summary || null,
        };
        try {
            const r = editing
                ? await api.patch(`/compounds/${slug}`, payload)
                : await api.post("/compounds", payload);
            toast.success(editing ? "Compuesto actualizado" : "Compuesto creado");
            navigate(`/compuestos/${r.data.slug}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al guardar");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={editing ? "Editar principio activo" : "Nuevo principio activo"}
                title={editing ? (name || "Editar compuesto") : "Añadir principio activo al catálogo"}
                lead="Define los datos básicos del compuesto. La ingesta automática añadirá la evidencia científica cuando se ejecute (cada 15 días o manualmente)."
            >
                <div className="mt-4">
                    <Link to="/compuestos" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]"><ArrowLeft size={14}/> Volver al catálogo</Link>
                </div>
            </PageHeader>

            <form onSubmit={submit} className="px-8 md:px-12 py-8 max-w-5xl space-y-6">
                <div className="border hairline bg-white p-6 space-y-4">
                    <div className="label-eyebrow">Datos básicos</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Nombre *">
                            <div className="flex gap-1.5">
                                <input required className="i" value={name} onChange={(e)=>setName(e.target.value)} data-testid="compound-name-input"/>
                                <button type="button" onClick={generateProfile} disabled={generating || !name.trim()}
                                    title="Generar todo el contenido con IA a partir del nombre"
                                    className="px-3 border hairline bg-[var(--cream)] hover:bg-white text-xs inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                                    data-testid="compound-ai-generate-button">
                                    <Sparkle size={13} className={generating ? "animate-pulse" : ""}/>
                                    {generating ? "Generando…" : "Generar con IA"}
                                </button>
                            </div>
                        </Field>
                        <Field label={editing ? "Slug (no editable)" : "Slug (opcional, se genera del nombre)"}>
                            <input className="i" value={slugVal} onChange={(e)=>setSlugVal(e.target.value)} disabled={editing} data-testid="compound-slug-input"/>
                        </Field>
                        <Field label="Categoría *"><input required className="i" value={category} onChange={(e)=>setCategory(e.target.value)} placeholder="antioxidante / antiinflamatorio / vitamina / mineral / aminoácido…" data-testid="compound-category-input"/></Field>
                        <Field label="Upper Limit (UL) en mg"><input type="number" step="any" className="i" value={upperLimit} onChange={(e)=>setUpperLimit(e.target.value)} data-testid="compound-ul-input"/></Field>
                    </div>
                    <Field label="Nombres alternativos (separados por coma)"><input className="i" value={aka} onChange={(e)=>setAka(e.target.value)} placeholder="Vit. C, Ácido ascórbico" data-testid="compound-aka-input"/></Field>
                    <Field label="Indicaciones (separadas por coma) *"><input required className="i" value={indications} onChange={(e)=>setIndications(e.target.value)} placeholder="antioxidante, síntesis de colágeno, inmunidad" data-testid="compound-indications-input"/></Field>
                </div>

                <div className="border hairline bg-white p-6 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="label-eyebrow">Dosis por indicación · {doseRanges.length}</div>
                        <button type="button" onClick={addDose} className="px-3 py-1.5 border hairline text-xs bg-[var(--cream)] hover:bg-white inline-flex items-center gap-1" data-testid="compound-add-dose"><Plus size={12}/> Añadir dosis</button>
                    </div>
                    {doseRanges.length === 0 && <p className="text-xs text-[var(--ink-3)] py-2">Sin rangos de dosis definidos.</p>}
                    {doseRanges.map((d, i) => (
                        <div key={i} className="grid sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`compound-dose-${i}`}>
                            <input className="i" placeholder="Indicación" value={d.indication} onChange={(e)=>updateDose(i,"indication",e.target.value)}/>
                            <input className="i" type="number" step="any" placeholder="min mg" value={d.min_mg} onChange={(e)=>updateDose(i,"min_mg",e.target.value)}/>
                            <input className="i" type="number" step="any" placeholder="típica mg" value={d.typical_mg} onChange={(e)=>updateDose(i,"typical_mg",e.target.value)}/>
                            <input className="i" type="number" step="any" placeholder="máx mg" value={d.max_mg} onChange={(e)=>updateDose(i,"max_mg",e.target.value)}/>
                            <input className="i" placeholder="frecuencia" value={d.frequency} onChange={(e)=>updateDose(i,"frequency",e.target.value)}/>
                            <button type="button" onClick={()=>removeDose(i)} className="text-[var(--terra-2)] p-2" data-testid={`compound-remove-dose-${i}`}><Trash size={14}/></button>
                        </div>
                    ))}
                </div>

                <div className="border hairline bg-white p-6 space-y-4">
                    <div className="label-eyebrow">Seguridad y normativa</div>
                    <Field label="Contraindicaciones (una por línea)"><textarea rows={3} className="i resize-none" value={contraindications} onChange={(e)=>setContraindications(e.target.value)} data-testid="compound-contraindications-input"/></Field>
                    <Field label="Interacciones (una por línea)"><textarea rows={3} className="i resize-none" value={interactions} onChange={(e)=>setInteractions(e.target.value)} data-testid="compound-interactions-input"/></Field>
                    <Field label="Notas regulatorias UE"><textarea rows={2} className="i resize-none" value={euNotes} onChange={(e)=>setEuNotes(e.target.value)} placeholder="EFSA opinion, RD 1487/2009…" data-testid="compound-eu-input"/></Field>
                    <Field label="Resumen del compuesto"><textarea rows={3} className="i resize-none" value={summary} onChange={(e)=>setSummary(e.target.value)} data-testid="compound-summary-input"/></Field>
                </div>

                <div className="border-t hairline pt-6 flex justify-end">
                    <button type="submit" disabled={submitting || !name.trim() || !category.trim()} className="btn-primary disabled:opacity-50" data-testid="compound-submit-button">
                        {submitting ? "Guardando…" : (editing ? "Guardar cambios" : "Crear principio activo")}
                    </button>
                </div>
            </form>
            <style>{`.i{width:100%;padding:9px 12px;border:1px solid var(--line);background:#fff;font-size:.875rem;outline:none}.i:focus{border-color:var(--sage)}.i:disabled{background:var(--cream);color:var(--ink-3)}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (<div><label className="text-xs text-[var(--ink-2)] block mb-1.5">{label}</label>{children}</div>);
}
